import { useMemo } from 'react';

import { GatewayChatLayout, useGatewayChatPage } from '@verantos-dev/feature-agent-chat';
import {
  useMappingReviewDialog,
  CombinedMappingDialog,
  useOmopSessionListener,
} from '@verantos-dev/feature-code-mapping';

/**
 * CodeListMapperPage Component
 *
 * This page provides a chat-based interface for code list mapping using the Gateway agent.
 *
 * FEATURE INTEGRATION: Mapping Review Dialog
 * ===========================================
 *
 * AUTO-TRIGGERED WORKFLOW:
 * When the backend OMOP mapping agent completes a mapping session, it emits an
 * OMOP_SESSION_METADATA event with the session_id and file_name. This page
 * automatically listens for that event and opens the mapping review dialog.
 *
 * WORKFLOW:
 * 1. User uploads CSV file via chat
 * 2. Agent processes codes and saves results to S3
 * 3. Agent emits OMOP_SESSION_METADATA event
 * 4. Frontend dialog automatically opens with session data
 * 5. User reviews and confirms mappings
 *
 * ENTRY POINT FOR NEW DEVELOPERS:
 * ================================
 *
 * 1. The mapping review dialog is managed by `useMappingReviewDialog` hook
 * 2. The `useOmopSessionListener` hook listens for session events
 * 3. When session event arrives, dialog automatically opens with real data
 * 4. See MappingWorkflowDialog.tsx for full component documentation
 */

export const CodeListMapperPage = () => {
  // ==========================================================================
  // MAPPING REVIEW DIALOG INTEGRATION
  // ==========================================================================

  const dialogProps = useMappingReviewDialog();
  const { handleOpenDialog } = dialogProps;

  // Listen for OMOP session events and auto-open dialog with session data
  useOmopSessionListener(handleOpenDialog, true);

  // ==========================================================================
  // GATEWAY CHAT PAGE SETUP
  // ==========================================================================

  const { layoutProps: originalLayoutProps } = useGatewayChatPage({
    baseOptions: {
      isOwner: true,
    },
    titleFallback: 'Code list mapper',
    disclaimerText: 'Experimental workflow for code list mapping. Validate results before use.',
    showProtocolMonitor: true,
  });

  const layoutProps = useMemo(() => {
    return {
      ...originalLayoutProps,
    };
  }, [originalLayoutProps]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <>
      <GatewayChatLayout
        sessionsPanelProps={layoutProps.sessionsPanelProps}
        chatWindowProps={layoutProps.chatWindowProps}
        protocolMonitorProps={layoutProps.protocolMonitorProps}
        showProtocolMonitor={layoutProps.showProtocolMonitor}
      />

      {/*
        Combined Mapping Dialog
        
        This dialog is conditionally rendered based on the isDialogOpen state from its hook.
        It can be opened in two ways:
        
        1. AUTOMATIC (Primary):
           - When backend emits OMOP_SESSION_METADATA event
           - useOmopSessionListener hook detects it and opens dialog
           - Dialog automatically fetches results from S3
        
        2. MANUAL (Development/Testing):
           - Via the temporary "Review Mappings" button
           - For testing with mock data or manual triggers
        
        The dialog automatically handles:
        - Opening/closing
        - Loading mapping results from S3 or mock
        - Tabbed interface with mapping results and final concept list
        - Export functionality
        
        See CombinedMappingDialog.tsx for implementation details.
      */}
      {dialogProps.isDialogOpen && <CombinedMappingDialog {...dialogProps} />}
    </>
  );
};

export default CodeListMapperPage;
